import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API } from "@/context/AuthContext";
import { GSTExtractBar } from "@/components/gst/GSTExtractBar";
import { GenerateBar, GSTR1Panel, GSTR3BPanel } from "@/components/gst/GSTPanels";
import { GSTR3BLedgerCheck } from "@/components/gst/GSTR3BLedgerCheck";
import { GSTR2BImportCard } from "@/components/gst/GSTR2BImportCard";
import { GSTR2AImportCard } from "@/components/gst/GSTR2AImportCard";

const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const todayISO = () => new Date().toISOString().slice(0, 10);

const download = async (reportId, fallback) => {
  try {
    const r = await axios.get(`${API}/gst-returns/reports/${reportId}/download`, { responseType: "blob" });
    const name = (r.headers["content-disposition"] || "").match(/filename="?([^";]+)/)?.[1] || fallback;
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = name; a.click(); URL.revokeObjectURL(a.href);
  } catch (e) { toast.error("Download failed — please try again"); }
};

export default function GSTReturns() {
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayISO());
  const [extracts, setExtracts] = useState([]);
  const [current, setCurrent] = useState(null);
  const [reports, setReports] = useState({});
  const [busy, setBusy] = useState("");

  const loadExtracts = useCallback(async () => {
    const r = await axios.get(`${API}/gst-returns/extracts`);
    setExtracts(r.data);
    return r.data;
  }, []);

  useEffect(() => { loadExtracts().then((list) => { if (list[0]) select(list[0]); }).catch(() => {}); }, [loadExtracts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!current || current.status !== "running") return undefined;
    const id = setTimeout(() => axios.get(`${API}/gst-returns/extracts/${current.id}`).then((r) => {
      setCurrent(r.data);
      if (r.data.status !== "running") loadExtracts();
    }).catch(() => {}), 2500);
    return () => clearTimeout(id);
  }, [current, loadExtracts]);

  const select = async (e) => {
    setCurrent(e); setFrom(e.from); setTo(e.to); setReports({});
    if (e.status === "done") {
      try { setReports((await axios.get(`${API}/gst-returns/extracts/${e.id}/reports`)).data); } catch { /* none yet */ }
    }
  };

  const extract = async () => {
    setBusy("extract");
    try {
      const r = await axios.post(`${API}/gst-returns/extract`, { from_date: from, to_date: to });
      setReports({}); setCurrent({ id: r.data.extract_id, status: "running", stage: "Queued", from, to });
      loadExtracts();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start extraction"); } finally { setBusy(""); }
  };

  const generate = async (kind) => {
    setBusy(kind);
    try {
      const r = await axios.post(`${API}/gst-returns/extracts/${current.id}/generate/${kind}`);
      setReports((p) => ({ ...p, [kind]: r.data }));
      toast.success(`${kind === "gstr1" ? "GSTR-1" : "GSTR-3B"} generated`);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); } finally { setBusy(""); }
  };

  const remove = async (e) => {
    try {
      await axios.delete(`${API}/gst-returns/extracts/${e.id}`);
      if (current?.id === e.id) { setCurrent(null); setReports({}); }
      loadExtracts();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete"); }
  };

  const ready = current?.status === "done";
  return (
    <div className="space-y-6" data-testid="gst-returns-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><FileSpreadsheet className="h-6 w-6" />GST Returns — GSTR-1 & GSTR-3B</h1>
        <p className="text-sm text-slate-500 mt-1">Step 1 extracts the billing documents once from SAP (read-only) and saves them here. Step 2 generates GSTR-1 (filed first) and then GSTR-3B from the saved extraction — instantly, without going back to SAP.</p>
      </div>
      <GSTExtractBar from={from} to={to} setFrom={setFrom} setTo={setTo} onExtract={extract} busy={busy === "extract"} extracts={extracts} current={current} onSelect={select} onDelete={remove} />

      {ready && (
        <Tabs defaultValue="gstr1" data-testid="gst-tabs">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="gstr1" data-testid="gst-tab-gstr1">GSTR-1 · Outward supplies</TabsTrigger>
            <TabsTrigger value="gstr3b" data-testid="gst-tab-gstr3b">GSTR-3B · Summary return</TabsTrigger>
          </TabsList>
          <TabsContent value="gstr1" className="space-y-6 mt-4">
            <GenerateBar kind="gstr1" label="GSTR-1" report={reports.gstr1} busy={busy === "gstr1"} onGenerate={() => generate("gstr1")} onDownload={() => download(reports.gstr1.id, "GSTR1.xlsx")}
                         note="Step 2 · builds the B2B / B2CL / B2CS / CDNR / Export / HSN / Docs tables into template V2.2 and matches output tax with the Trial Balance." />
            <GSTR1Panel report={reports.gstr1} from={current.from} to={current.to} />
          </TabsContent>
          <TabsContent value="gstr3b" className="space-y-6 mt-4">
            <GenerateBar kind="gstr3b" label="GSTR-3B" report={reports.gstr3b} busy={busy === "gstr3b"} onGenerate={() => generate("gstr3b")} onDownload={() => download(reports.gstr3b.id, "GSTR3B.xlsm")}
                         note="Step 3 · fills 3.1 (a)–(d), 3.2, Table 4 ITC (from the GSTR-2 register, posting date) and 6.1 payment of tax in the macro-enabled GSTR-3B utility. File GSTR-1 first, then GSTR-3B." />
            <GSTR3BPanel report={reports.gstr3b} />
            <GSTR2BImportCard extract={current} />
            <GSTR2AImportCard extract={current} />
            <GSTR3BLedgerCheck extractId={current?.id} report={reports.gstr3b} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
